import React, { useState, useEffect } from 'react';
import { IntegrationRequest } from '../../lib/tenant-api';
import Modal from '../Modal';
import type { IntegrationConfigProps } from './types';

export const WordpressConfig: React.FC<IntegrationConfigProps> = ({
  isOpen,
  onClose,
  integration,
  createIntegrationMutation,
  updateIntegrationMutation
}) => {
  const [formData, setFormData] = useState({
    name: '',
    site_url: '',
    username: '',
    app_password: '',
    connection_type: 'self_hosted' as 'self_hosted' | 'wordpress_com',
    is_enabled: true,
    endpoints: {
      products: ['/wp-json/wc/v3/products'],
      services: ['/wp-json/wp/v2/pages?service=1'],
      faqs: ['/wp-json/wp/v2/posts']
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!integration;

  // Update default endpoints based on connection type
  const getDefaultEndpoints = (connectionType: 'self_hosted' | 'wordpress_com') => {
    if (connectionType === 'wordpress_com') {
      return {
        products: ['/posts'], // Assuming products are posts or custom post types accessible via posts endpoint
        services: ['/posts?type=page'], // Assuming services are pages, which are a type of post
        faqs: ['/posts'] // Assuming FAQs are posts or pages
      };
    }
    return {
      products: ['/wp-json/wc/v3/products'],
      services: ['/wp-json/wp/v2/pages?service=1'],
      faqs: ['/wp-json/wp/v2/posts']
    };
  };

  useEffect(() => {
    if (integration) {
      setFormData({
        name: integration.name,
        site_url: integration.config.site_url || '',
        username: integration.config.username || '',
        app_password: integration.config.app_password || '',
        connection_type: integration.config.connection_type || 'self_hosted',
        is_enabled: integration.is_enabled,
        endpoints: {
          products: integration.config.endpoints?.products || ['/wp-json/wc/v3/products'],
          services: integration.config.endpoints?.services || ['/wp-json/wp/v2/pages?service=1'],
          faqs: integration.config.endpoints?.faqs || ['/wp-json/wp/v2/posts']
        }
      });
    } else {
      setFormData({
        name: '',
        site_url: '',
        username: '',
        app_password: '',
        connection_type: 'self_hosted',
        is_enabled: true,
        endpoints: {
          products: ['/wp-json/wc/v3/products'],
          services: ['/wp-json/wp/v2/pages?service=1'],
          faqs: ['/wp-json/wp/v2/posts']
        }
      });
    }
    setErrors({});
  }, [integration, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Integration name is required';
    }

    if (!formData.site_url.trim()) {
      newErrors.site_url = 'Site URL is required';
    } else {
      try {
        new URL(formData.site_url);
      } catch {
        newErrors.site_url = 'Please enter a valid URL';
      }
    }

    if (formData.connection_type === 'self_hosted') {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required for self-hosted WordPress';
      }
      if (!formData.app_password.trim()) {
        newErrors.app_password = 'Application password is required for self-hosted WordPress';
      }
    } else {
      if (!formData.app_password.trim()) {
        newErrors.app_password = 'WordPress.com API token is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const integrationData: IntegrationRequest = {
      integration_type: 'wordpress',
      name: formData.name,
      config: {
        site_url: formData.site_url.replace(/\/$/, ''),
        username: formData.username,
        app_password: formData.app_password,
        connection_type: formData.connection_type,
        endpoints: formData.endpoints,
      },
      is_enabled: formData.is_enabled,
    };

    if (isEditing && integration) {
      updateIntegrationMutation.mutate({
        id: integration.id,
        data: integrationData,
      });
    } else {
      createIntegrationMutation.mutate(integrationData);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      // Update endpoints when connection type changes
      if (field === 'connection_type') {
        newData.endpoints = getDefaultEndpoints(value as 'self_hosted' | 'wordpress_com');
      }

      return newData;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      site_url: '',
      username: '',
      app_password: '',
      connection_type: 'self_hosted',
      is_enabled: true,
      endpoints: {
        products: ['/wp-json/wc/v3/products'],
        services: ['/wp-json/wp/v2/pages?service=1'],
        faqs: ['/wp-json/wp/v2/posts']
      }
    });
    setErrors({});
    onClose();
  };

  const isLoading = createIntegrationMutation.isLoading || updateIntegrationMutation.isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? `Edit WordPress integration` : `Connect WordPress`}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Integration Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Integration Name
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
            placeholder="e.g., My WordPress Site"
            disabled={isLoading}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* Connection Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            WordPress Connection Type
          </label>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="radio"
                id="self_hosted"
                name="connection_type"
                value="self_hosted"
                checked={formData.connection_type === 'self_hosted'}
                onChange={(e) => handleInputChange('connection_type', e.target.value)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                disabled={isLoading}
              />
              <label htmlFor="self_hosted" className="ml-3 block text-sm text-gray-900">
                <span className="font-medium">Self-Hosted WordPress</span>
                <span className="block text-gray-500 text-xs mt-1">
                  For WordPress sites hosted on your own server or hosting provider
                </span>
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="wordpress_com"
                name="connection_type"
                value="wordpress_com"
                checked={formData.connection_type === 'wordpress_com'}
                onChange={(e) => handleInputChange('connection_type', e.target.value)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                disabled={isLoading}
              />
              <label htmlFor="wordpress_com" className="ml-3 block text-sm text-gray-900">
                <span className="font-medium">WordPress.com</span>
                <span className="block text-gray-500 text-xs mt-1">
                  For sites hosted directly on WordPress.com (e.g., yoursite.wordpress.com)
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Site URL */}
        <div>
          <label htmlFor="site_url" className="block text-sm font-medium text-gray-700 mb-2">
            WordPress Site URL
          </label>
          <input
            type="url"
            id="site_url"
            value={formData.site_url}
            onChange={(e) => handleInputChange('site_url', e.target.value)}
            className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.site_url ? 'border-red-300' : 'border-gray-300'
              }`}
            placeholder={formData.connection_type === 'wordpress_com' ? 'https://yoursite.wordpress.com' : 'https://your-wordpress-site.com'}
            disabled={isLoading}
          />
          {errors.site_url && <p className="mt-1 text-sm text-red-600">{errors.site_url}</p>}
        </div>

        {/* Username - only for self-hosted */}
        {formData.connection_type === 'self_hosted' && (
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              WordPress Username
            </label>
            <input
              type="text"
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.username ? 'border-red-300' : 'border-gray-300'
                }`}
              placeholder="Your WordPress username"
              disabled={isLoading}
            />
            {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
          </div>
        )}

        {/* Application Password / API Token */}
        <div>
          <label htmlFor="app_password" className="block text-sm font-medium text-gray-700 mb-2">
            {formData.connection_type === 'wordpress_com' ? 'WordPress.com API Token' : 'Application Password'}
          </label>
          <input
            type="password"
            id="app_password"
            value={formData.app_password}
            onChange={(e) => handleInputChange('app_password', e.target.value)}
            className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.app_password ? 'border-red-300' : 'border-gray-300'
              }`}
            placeholder={formData.connection_type === 'wordpress_com' ? 'Your WordPress.com API token' : 'Your WordPress application password'}
            disabled={isLoading}
          />
          {errors.app_password && <p className="mt-1 text-sm text-red-600">{errors.app_password}</p>}
          <p className="mt-2 text-sm text-gray-500">
            {formData.connection_type === 'wordpress_com'
              ? 'Generate an API token from your WordPress.com account settings or developer dashboard'
              : 'Generate an application password in your WordPress dashboard under Users → Profile → Application Passwords'
            }
          </p>
        </div>

        {/* Enable Integration */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_enabled"
            checked={formData.is_enabled}
            onChange={(e) => handleInputChange('is_enabled', e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 "
            disabled={isLoading}
          />
          <label htmlFor="is_enabled" className="ml-2 block text-sm text-gray-900">
            Enable this integration
          </label>
        </div>

        {/* Help Text */}
        <div className="bg-indigo-50 border border-indigo-200  p-4">
          <h4 className="text-sm font-medium text-indigo-800 mb-2">
            How to set up {formData.connection_type === 'wordpress_com' ? 'WordPress.com' : 'self-hosted WordPress'} integration:
          </h4>
          {formData.connection_type === 'wordpress_com' ? (
            <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside">
              <li>Go to your WordPress.com account settings</li>
              <li>Navigate to the "Security" or "Developer" section</li>
              <li>Generate a new API token for this application</li>
              <li>Copy the generated token and paste it above</li>
              <li>Note: Some WordPress.com plans may have API limitations</li>
            </ol>
          ) : (
            <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside">
              <li>Log in to your WordPress admin dashboard</li>
              <li>Go to Users → Your Profile</li>
              <li>Scroll down to "Application Passwords"</li>
              <li>Enter a name for this application (e.g., "AI Assistant")</li>
              <li>Click "Add New Application Password"</li>
              <li>Copy the generated password and paste it above</li>
            </ol>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditing ? 'Updating...' : 'Connecting...'}
              </>
            ) : (
              isEditing ? 'Update Integration' : 'Connect WordPress'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
